import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import { deliveryChallanService } from '../../../services/deliveryChallanService';
import { salesOrderService } from '../../../services/salesOrderService';
import { 
  DeliveryChallan, 
  DeliveryChallanCreate, 
  DeliveryChallanUpdate,
  DeliveryChallanFromSalesOrderRequest
} from '../../../types/deliveryChallan';
import { SalesOrder } from '../../../types/salesOrder';
import { formatCurrency } from '../../../utils/formatters';

interface DeliveryChallanFormData {
  sales_order_id: string;
  customer_id: string;
  challan_date: string;
  dispatch_date: string;
  shipping_address_id: string;
  transporter_name: string;
  vehicle_number: string;
  lr_number: string;
  e_way_bill_number: string;
  notes: string;
}

const DeliveryChallanForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const salesOrderId = searchParams.get('sales_order_id');

  const [formData, setFormData] = useState<DeliveryChallanFormData>({
    sales_order_id: salesOrderId || '',
    customer_id: '',
    challan_date: format(new Date(), 'yyyy-MM-dd'),
    dispatch_date: '',
    shipping_address_id: '',
    transporter_name: '',
    vehicle_number: '',
    lr_number: '',
    e_way_bill_number: '',
    notes: ''
  });

  const [selectedSalesOrder, setSelectedSalesOrder] = useState<SalesOrder | null>(null);
  const [availableSalesOrders, setAvailableSalesOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = Boolean(id);
  const isFromSalesOrder = Boolean(salesOrderId);

  // Fetch delivery challan data for editing
  useEffect(() => {
    const fetchDeliveryChallan = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const deliveryChallan = await deliveryChallanService.getDeliveryChallan(id);
        
        setFormData({
          sales_order_id: deliveryChallan.sales_order_id,
          customer_id: deliveryChallan.customer_id,
          challan_date: deliveryChallan.challan_date,
          dispatch_date: deliveryChallan.dispatch_date || '',
          shipping_address_id: deliveryChallan.shipping_address_id,
          transporter_name: deliveryChallan.transporter_name || '',
          vehicle_number: deliveryChallan.vehicle_number || '',
          lr_number: deliveryChallan.lr_number || '',
          e_way_bill_number: deliveryChallan.e_way_bill_number || '',
          notes: deliveryChallan.notes || ''
        });

        if (deliveryChallan.sales_order) {
          setSelectedSalesOrder(deliveryChallan.sales_order as SalesOrder);
        }
      } catch (error: any) {
        console.error('Failed to fetch delivery challan:', error);
        setError('Failed to load delivery challan');
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveryChallan();
  }, [id]);

  // Fetch sales order if coming from sales order page
  useEffect(() => {
    const fetchSalesOrder = async () => {
      if (!salesOrderId) return;

      try {
        setLoading(true);
        const salesOrder = await salesOrderService.getSalesOrder(salesOrderId);
        setSelectedSalesOrder(salesOrder);
        setFormData(prev => ({
          ...prev,
          sales_order_id: salesOrder.id,
          customer_id: salesOrder.customer_id,
          shipping_address_id: salesOrder.shipping_address_id || ''
        }));
      } catch (error: any) {
        console.error('Failed to fetch sales order:', error);
        setError('Failed to load sales order');
      } finally {
        setLoading(false);
      }
    };

    fetchSalesOrder();
  }, [salesOrderId]);

  // Fetch available sales orders for selection
  useEffect(() => {
    const fetchSalesOrders = async () => {
      if (isEdit || isFromSalesOrder) return;

      try {
        const response = await salesOrderService.getSalesOrders({
          status: 'confirmed' as any // Only confirmed orders can have delivery challans
        });
        setAvailableSalesOrders(response.sales_orders);
      } catch (error: any) {
        console.error('Failed to fetch sales orders:', error);
      }
    };

    fetchSalesOrders();
  }, [isEdit, isFromSalesOrder]);

  const handleInputChange = (field: keyof DeliveryChallanFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSalesOrderChange = async (salesOrder: SalesOrder | null) => {
    if (!salesOrder) {
      setSelectedSalesOrder(null);
      setFormData(prev => ({
        ...prev,
        sales_order_id: '',
        customer_id: '',
        shipping_address_id: ''
      }));
      return;
    }

    setSelectedSalesOrder(salesOrder);
    setFormData(prev => ({
      ...prev,
      sales_order_id: salesOrder.id,
      customer_id: salesOrder.customer_id,
      shipping_address_id: salesOrder.shipping_address_id || ''
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.sales_order_id) {
      enqueueSnackbar('Please select a sales order', { variant: 'error' });
      return false;
    }
    if (!formData.challan_date) {
      enqueueSnackbar('Please enter challan date', { variant: 'error' });
      return false;
    }
    if (!formData.shipping_address_id) {
      enqueueSnackbar('Please select shipping address', { variant: 'error' });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      if (isEdit && id) {
        // Update existing delivery challan
        const updateData: DeliveryChallanUpdate = {
          challan_date: formData.challan_date,
          dispatch_date: formData.dispatch_date || undefined,
          shipping_address_id: formData.shipping_address_id,
          transporter_name: formData.transporter_name || undefined,
          vehicle_number: formData.vehicle_number || undefined,
          lr_number: formData.lr_number || undefined,
          e_way_bill_number: formData.e_way_bill_number || undefined,
          notes: formData.notes || undefined
        };

        await deliveryChallanService.updateDeliveryChallan(id, updateData);
        enqueueSnackbar('Delivery challan updated successfully', { variant: 'success' });
        navigate(`/admin/documents/delivery-challans/${id}`);
      } else {
        // Create new delivery challan from sales order
        const createData: DeliveryChallanFromSalesOrderRequest = {
          sales_order_id: formData.sales_order_id,
          challan_date: formData.challan_date || undefined,
          dispatch_date: formData.dispatch_date || undefined,
          transporter_name: formData.transporter_name || undefined,
          vehicle_number: formData.vehicle_number || undefined,
          lr_number: formData.lr_number || undefined,
          e_way_bill_number: formData.e_way_bill_number || undefined,
          notes: formData.notes || undefined
        };

        const result = await deliveryChallanService.createFromSalesOrder(createData);
        enqueueSnackbar(`Delivery challan ${result.challan_number} created successfully`, { variant: 'success' });
        navigate(`/admin/documents/delivery-challans/${result.delivery_challan_id}`);
      }
    } catch (error: any) {
      console.error('Failed to save delivery challan:', error);
      enqueueSnackbar('Failed to save delivery challan', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/admin/dashboard">
          Dashboard
        </Link>
        <Link color="inherit" href="/admin/documents/delivery-challans">
          Delivery Challans
        </Link>
        <Typography color="text.primary">
          {isEdit ? 'Edit' : 'Create'} Delivery Challan
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/admin/documents/delivery-challans')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            {isEdit ? 'Edit' : 'Create'} Delivery Challan
          </Typography>
        </Box>
        
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin/documents/delivery-challans')}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Delivery Challan'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Form */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Delivery Challan Information
              </Typography>
              
              <Grid container spacing={2}>
                {/* Sales Order Selection */}
                <Grid item xs={12}>
                  {isEdit || isFromSalesOrder ? (
                    <TextField
                      fullWidth
                      label="Sales Order"
                      value={selectedSalesOrder?.order_number || ''}
                      disabled
                      helperText="Sales order cannot be changed after creation"
                    />
                  ) : (
                    <Autocomplete
                      options={availableSalesOrders}
                      getOptionLabel={(option) => `${option.order_number} - ${option.customer?.billing_name || option.customer?.name}`}
                      value={selectedSalesOrder}
                      onChange={(_, value) => handleSalesOrderChange(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Sales Order"
                          required
                          helperText="Select a confirmed sales order"
                        />
                      )}
                    />
                  )}
                </Grid>

                {/* Dates */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Challan Date"
                    type="date"
                    value={formData.challan_date}
                    onChange={(e) => handleInputChange('challan_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Dispatch Date"
                    type="date"
                    value={formData.dispatch_date}
                    onChange={(e) => handleInputChange('dispatch_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave empty if not yet dispatched"
                  />
                </Grid>

                {/* Transport Details */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                    Transport Details
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Transporter Name"
                    value={formData.transporter_name}
                    onChange={(e) => handleInputChange('transporter_name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Vehicle Number"
                    value={formData.vehicle_number}
                    onChange={(e) => handleInputChange('vehicle_number', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="LR Number"
                    value={formData.lr_number}
                    onChange={(e) => handleInputChange('lr_number', e.target.value)}
                    helperText="Lorry Receipt Number"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="E-Way Bill Number"
                    value={formData.e_way_bill_number}
                    onChange={(e) => handleInputChange('e_way_bill_number', e.target.value)}
                  />
                </Grid>

                {/* Notes */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sales Order Summary */}
        <Grid item xs={12} md={4}>
          {selectedSalesOrder && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sales Order Summary
                </Typography>
                
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Order Number
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {selectedSalesOrder.order_number}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body1">
                    {selectedSalesOrder.customer?.billing_name || selectedSalesOrder.customer?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedSalesOrder.customer?.contact_person}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Order Date
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(selectedSalesOrder.order_date), 'dd/MM/yyyy')}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Total Amount
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(selectedSalesOrder.total_amount)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Line Items ({selectedSalesOrder.items.length})
                </Typography>
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedSalesOrder.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              {item.variant?.product?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.variant?.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {item.quantity}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default DeliveryChallanForm;